import { createHmac, timingSafeEqual } from "node:crypto";

const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface TokenPayload {
  action: "approve" | "decline";
  exp: number;
  sponsorshipId: string;
}

function b64url(buf: Buffer | string): string {
  const b = typeof buf === "string" ? Buffer.from(buf, "utf8") : buf;
  return b.toString("base64url");
}

function getSecret(): string {
  const secret = process.env.SPONSORSHIP_APPROVAL_SECRET;
  if (!secret) {
    throw new Error("SPONSORSHIP_APPROVAL_SECRET env var is not set");
  }
  return secret;
}

function sign(payload: string, secret: string): string {
  return createHmac("sha256", secret).update(payload).digest("base64url");
}

export function generateSponsorshipToken(
  sponsorshipId: string,
  action: "approve" | "decline"
): string {
  const payload: TokenPayload = {
    sponsorshipId,
    action,
    exp: Date.now() + EXPIRY_MS,
  };
  const encoded = b64url(JSON.stringify(payload));
  const sig = sign(encoded, getSecret());
  return `${encoded}.${sig}`;
}

export function verifySponsorshipToken(
  token: string
): { sponsorshipId: string; action: "approve" | "decline" } | null {
  const dot = token.lastIndexOf(".");
  if (dot === -1) {
    return null;
  }

  const encoded = token.slice(0, dot);
  const sig = token.slice(dot + 1);

  let secret: string;
  try {
    secret = getSecret();
  } catch {
    return null;
  }

  const expected = sign(encoded, secret);

  // Constant-time comparison to prevent timing attacks
  const sigBuf = Buffer.from(sig, "base64url");
  const expectedBuf = Buffer.from(expected, "base64url");
  if (
    sigBuf.length !== expectedBuf.length ||
    !timingSafeEqual(sigBuf, expectedBuf)
  ) {
    return null;
  }

  let payload: TokenPayload;
  try {
    payload = JSON.parse(
      Buffer.from(encoded, "base64url").toString("utf8")
    ) as TokenPayload;
  } catch {
    return null;
  }

  if (Date.now() > payload.exp) {
    return null;
  }
  if (payload.action !== "approve" && payload.action !== "decline") {
    return null;
  }
  if (typeof payload.sponsorshipId !== "string" || !payload.sponsorshipId) {
    return null;
  }

  return { sponsorshipId: payload.sponsorshipId, action: payload.action };
}
